/* eslint-disable array-callback-return */
/* eslint-disable react-hooks/exhaustive-deps */
import AuditSheet from './components/AuditSheet';
import AuditSheetFooter from './components/AuditSheetFooter';
import Topbar from './components/Topbar';
import {
  Box,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  useBoolean,
  VStack,
  Text
} from '@chakra-ui/react';
import AppColors from '../../constants/AppColors';
import { withProvider } from '../../hoc/withProvider';
import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AuditSheetProvider, {
  AuditSheetContext,
} from '../../providers/AuditSheetProvider';
import { createEmptyObject } from './components/createEmptyObject';
import { AWAITING_AUDIT } from '../../constants/constants';
import { sanitize } from './components/sanitize';
import SubmitModal from './components/SubmitModal';
import ROUTES from '../../constants/Routes';
import { useToastr } from '@laxmimanogna/code-quick-components';
import { successToast } from '../../components/successToast';
import { submitRule } from './components/submitRules';
import CommentBox from './components/CommentBox';
import html2canvas from 'html2canvas';
import {
  arrangeData,
  createEmptyColumn,
  payloadreStructure,
  prepareAuditSheetColumn,
  prepareUpdatePayload,
} from '../../utils/auditsheet.utils';
import { AccessDenied } from '../dashboard/components/AccessDenied';
import { isSheetEditableBy } from './sheetAccess';
import { NAVIGATE_AWAY } from './components/BatchChartNavigator';
import { AuthContext } from '../../providers/AuthProvider';

// Returns true when a row field belongs to a mapped import prefix.
// prefix 'billed_icd' covers 'billed_icd', 'billed_icd_2', 'billed_icd_3', etc.
function isImportFieldMapped(fieldName, prefixes) {
  for (const prefix of prefixes) {
    if (fieldName === prefix || fieldName.startsWith(prefix + '_')) return true;
  }
  return false;
}

// Dynamic field families that can have numbered variants (_2, _3, …).
// When one of these is imported, ALL existing numbered variants are wiped first
// so stale old columns don't survive alongside the new import.
const DYNAMIC_FIELD_PREFIXES = [
  'billed_icd', 'srvcs_no', 'billed_modifier',
  'audited_icd', 'audited_cpt', 'audited_modifier',
];

// Merges imported rows with existing sheet rows:
// - matches by encounter_no (when mapped) or by position
// - overwrites only the fields whose prefix is in mappedPrefixes
// - clears stale numbered variants for any dynamic field that was imported
// - preserves all other existing field values whose prefix wasn't mapped this import
// - appends unmatched existing rows so the backend doesn't delete them
function mergeImportWithExisting(importedSheet, existingData, mappedPrefixes) {
  const existingReal = (existingData ?? []).filter(r => r?.id != null);
  if (existingReal.length === 0) return importedSheet;

  const matchByEncounterNo = mappedPrefixes.has('encounter_no');
  const existingByEncounter = {};
  if (matchByEncounterNo) {
    existingReal.forEach(row => {
      if (row.encounter_no != null) {
        existingByEncounter[String(row.encounter_no)] = row;
      }
    });
  }

  const matchedExistingIds = new Set();

  const mergedRows = importedSheet.data.map((importedRow, idx) => {
    const existingRow = matchByEncounterNo && importedRow.encounter_no != null
      ? (existingByEncounter[String(importedRow.encounter_no)] ?? null)
      : (existingReal[idx] ?? null);

    if (!existingRow) return importedRow;
    matchedExistingIds.add(existingRow.id);

    const merged = { ...existingRow };

    // Step 1: null out stale numbered variants for every dynamic prefix that
    // was imported — prevents old _2/_3 columns surviving alongside new data.
    DYNAMIC_FIELD_PREFIXES.forEach(prefix => {
      if (mappedPrefixes.has(prefix)) {
        Object.keys(existingRow).forEach(key => {
          if ((key === prefix || key.startsWith(prefix + '_')) && !(key in importedRow)) {
            merged[key] = null;
          }
        });
      }
    });

    // Step 2: apply all imported field values for mapped prefixes.
    Object.keys(importedRow).forEach(key => {
      if (isImportFieldMapped(key, mappedPrefixes)) {
        merged[key] = importedRow[key];
      }
    });

    return merged;
  });

  // Keep unmatched existing rows so the backend does not delete them
  const unmatchedExisting = existingReal.filter(r => !matchedExistingIds.has(r.id));

  return { ...importedSheet, data: [...mergedRows, ...unmatchedExisting] };
}

const AuditSheetContainer = ({ view, batch }) => {
  const auditSheetCount = Math.ceil(window.innerHeight / 24);  
  const location = useLocation();

  // refs
  const domEl = useRef(null);
  const topbar = useRef(null);
  const footer = useRef(null);
  const notesDrawerRef = useRef(null);

  // other hooks
  const { uploadId, batchId } = useParams();
  const navigate = useNavigate();
  const toast = useToastr();

  const [isOpen, ioState] = useBoolean(false);
  const [isLoading, ilState] = useBoolean(false);
  const [isCommentOpen, icoState] = useBoolean(false);

  const [lastSaved, setLastSaved] = useState('');
  const [sheets, setSheets] = useState([
    {
      index: 1,
      sheet_name: 'Sheet1',
      data: createEmptyObject(auditSheetCount),
    },
  ]);
  // Always 0: a chart has exactly one sheet now that the footer sheet tabs are
  // gone, so there is nothing left that can switch the active sheet.
  const [activeSheetIndex] = useState(0);
  const [scale, setScale] = useState(1);
  const [metaData, setMetaData] = useState({});
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [isSubmitDisabled, setSubmitDisabled] = useState(true);
  const [coords, setCoords] = useState({
    x: 0,
    y: 0,
  });

  const [batchName, setBatchName] = useState(null);
  const [errMsg, setErrMsg] = useState(null);

  // making sure that even tough the view or batch parameter is not passed, batch audit sheet are not editable
  // useEffect(() => {
  //   if (view) {
  //     return;
  //   }
  //   if (batch || batchId) {
  //     view = true;
  //   }
  // }, [batch, batchId])

  const auditSheetContext = useContext(AuditSheetContext);

  const { 
    getAuditUpload, 
    getAuditSheet, 
    currentUpload, 
    getIndustryCodes, 
    getICDCodes, 
    getModifiers, 
    getUsers, 
    users, 
    getAllUsers, 
    getCommentFlag, 
    providerOptions, 
    getComments, 
    setSheetData, 
    getRebuttalSubmitValidation, 
    allComments, 
    setChartBatchId ,
    chartBatchId,
    getBatchAuditSheet,
    isAccessDenied,
    isAuditSheetLoading,
  } = auditSheetContext;



  const [canAccessAuditSheet, setcanAccessAuditSheet] = useState(null);
  const [updatingAuditSheet, uasState] = useBoolean(false);
  const [outstandingAuditSheetUpdate, setOutstandingAuditSheetUpdate] = useState(null);
  const authContext = useContext(AuthContext)

 // get auditsheet user data
  const getUpload = async () => {
    if (batch) {
      if (!view) {
        navigate(`${location.pathname}/view`, { replace: true });
        view = true;
      }
      getSheet();
      return;
    }
    if (uploadId) {
      const res = await getAuditUpload(uploadId);
      if (res?.error) {
        setErrMsg(res.error);
        return;
      }
      setLastSaved(res.updated_date);
      if (!view) {
        // Anything this user cannot edit -- read only status, or a chart assigned
        // to another auditor -- opens on the view only route instead.
        if (!isSheetEditableBy(res, authContext.currentUser)) {
          navigate(`${location.pathname}/view`, { replace: true });
          return;
        }
        auditSheetContext.monitorAuditHour(uploadId);
      }
    }
  };

  // Build sheetData map: { rowId: rowNumber (1-based) } for location display in notes drawer
  useEffect(() => {
    const map = {};
    sheets.forEach(sheet => {
      sheet.data.forEach((row, idx) => {
        if (row.id != null) {
          map[row.id] = idx + 1;
        }
      });
    });
    setSheetData(map);
  }, [sheets]);

  const onCommentClick = async (rowIndex, rowData) => {
    if (!rowData?.id) return;
    setMetaData({ rowIndex, selectedRowData: rowData });
    await getComments(uploadId, { row: rowData.id });
    icoState.on();
  };

  const onSelectionEnd = (rowData) => {
    setSelectedRowData(rowData);
  };


  const onZoom = value => {
    let temp = 1;
    if (value >= 50) {
      temp = (2 * value) / 100;
    } else if (value === 0) {
      temp = 0.3;
    } else {
      temp = (value / 100) * 2;
    }
    setScale(temp);
  };

  // sheets Data || fetch data
    const getSheet = async () => {
      if (!isAccessDenied || batch) {
        if (uploadId) {
          const response = await getAuditSheet(uploadId, currentUpload);
          const sheetBatchId = response[0]?.batch_id || response[0]?.batch;
          if (sheetBatchId) setChartBatchId(sheetBatchId);
          
          if (response?.length) {
            const updated = response.map((res, i) => {
              const _reDestructuredData = createEmptyColumn(res.data);
  
              const reDestructuredData = _reDestructuredData.map(sd => {
                const tempObj = { ...sd, ...sd.additional_attributes };
                tempObj['audited_cpt'] = tempObj['audited_code'];
                delete tempObj.additional_attributes;
                delete tempObj?.audited_code;
  
                if (tempObj?.enc_dt) {
                    const parts = tempObj.enc_dt.split('-');
                    const year = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1;
                    const day = parseInt(parts[2], 10);
                    const parsedDate = new Date(year, month, day);
              
                    if (!isNaN(parsedDate.getTime())) {
                      const formattedDate = `${(parsedDate.getMonth() + 1).toString().padStart(2, '0')}-${parsedDate.getDate().toString().padStart(2, '0')}-${parsedDate.getFullYear()}`;
                      tempObj.enc_dt = formattedDate;
                    }
                }
                if (!tempObj.hasOwnProperty('icd_agree')){
                  tempObj['icd_agree'] = null
                }
                if (!tempObj.hasOwnProperty('icd_disagree')){
                  tempObj['icd_disagree'] = null
                }
  
                return tempObj;
              });
  
              const _arrangeData = arrangeData(reDestructuredData);
  
              const updatedData = [
                ..._arrangeData,
                ...createEmptyObject(auditSheetCount - res.data.length),
              ];
              return {
                ...res,
                index: i + 1,
                data: updatedData,
              };
            });
            setSheets(updated);
          }
        } else if (batch) {
            const response = await getBatchAuditSheet(batchId);
            if (response?.error) {
              setErrMsg(response.error);
              return;
            }
      
            if (response.length) {
              setBatchName(response[0].batch_name);
              const updated = response.map((res, i) => {
      
                const _reDestructuredData = createEmptyColumn(res.data);
      
                const reDestructuredData = _reDestructuredData.map(sd => {
                  const tempObj = { ...sd, ...sd.additional_attributes };
                  tempObj['audited_cpt'] = tempObj['audited_code'];
                  delete tempObj.additional_attributes;
                  delete tempObj?.audited_code;
      
                  if (tempObj?.enc_dt) {
                    const parts = tempObj.enc_dt.split('-');
                    const year = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1;
                    const day = parseInt(parts[2], 10);
                    const parsedDate = new Date(year, month, day);
      
                    if (!isNaN(parsedDate.getTime())) {
                      const formattedDate = `${(parsedDate.getMonth() + 1)
                        .toString()
                        .padStart(2, '0')}-${parsedDate
                        .getDate()
                        .toString()
                        .padStart(2, '0')}-${parsedDate.getFullYear()}`;
                      tempObj.enc_dt = formattedDate;
                    }
                  }
                  if (!tempObj.hasOwnProperty('icd_agree')){
                    tempObj['icd_agree'] = null
                  }
                  if (!tempObj.hasOwnProperty('icd_disagree')){
                    tempObj['icd_disagree'] = null
                  }
                  return tempObj;
                });
      
      
                const _arrangeData = arrangeData(reDestructuredData);
      
      
                const updatedData = [
                  ..._arrangeData,
                  ...createEmptyObject(auditSheetCount - res.data.length),
                ];
                return {
                  ...res,
                  index: i + 1,
                  data: updatedData,
                };
              });
      
              setSheets(updated);
              disableSubmitFunc(response)
      
            }
          };
        }
    };

  useEffect(() => {
      if(!isAccessDenied){
        auditSheetContext.fetchResponseAuditIssues();
      }
      if(!isAccessDenied && !view){
        getIndustryCodes();
        getICDCodes();
        getModifiers();
        getUsers()
        getAllUsers()
        if (uploadId) {
          auditSheetContext.fetchProviderDropdown(uploadId)
        }
      }
    }, [isAccessDenied]);

  useEffect(() => {
      if(!isAccessDenied){
        if (uploadId || batchId) {
          getCommentFlag({ uploadId, batchId })
        }
      }
    }, [uploadId, batchId, isAccessDenied])

  useEffect(() => {
    getUpload();
  }, []);

  useEffect(()=>{
      // performing get sheet for single audit sheet view because batch already has their sheet called
      if (!isAccessDenied && uploadId) {
        getSheet();
      }
  
    },[uploadId, isAccessDenied])

  useEffect(() => {
    /**
     * sanitize() will add old_rendering and asign as id to rendering and remove empty rows
     */
    if (!isAccessDenied) {
      const sanitizedSheet = sanitize(sheets, providerOptions);
      disableSubmitFunc(sanitizedSheet);
    }
    
  }, [isAccessDenied, sheets]);

  const disableSubmitFunc = sanitizedSheet => {
    // For REBUTTAL IN PROGRESS, skip data validation — audit was already validated
    // when first submitted. Auditor just needs to respond to rebuttals and re-submit.
    if (currentUpload?.status === 'REBUTTAL IN PROGRESS') {
      setSubmitDisabled(false);
      return;
    }

    const isEverySanitizedSheetEmpty = sanitizedSheet.every(
      sheet => sheet.data.length === 0
    );

    if (isEverySanitizedSheetEmpty) {
      setSubmitDisabled(true);
    } else {
      const isSubmitable = submitRule(sanitizedSheet);
      setSubmitDisabled(isSubmitable);
    }
  };

  const downloadImage = async () => {
    // download image
    function dataURIToBlob(dataURI) {
      const splitDataURI = dataURI.split(',');
      const byteString =
        splitDataURI[0].indexOf('base64') >= 0
          ? atob(splitDataURI[1])
          : decodeURI(splitDataURI[1]);
      const mimeString = splitDataURI[0].split(':')[1].split(';')[0];

      const ia = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++)
        ia[i] = byteString.charCodeAt(i);

      return new Blob([ia], { type: mimeString });
    }
    const canvas = await html2canvas(domEl.current);
    const dataUrl = canvas.toDataURL('image/png', 1.0);
    return dataURIToBlob(dataUrl);
  };

  const updateCurrentAuditImage = async () => {
    try {
      //submit screenshot
      const file = await downloadImage();
      const fd = new FormData();
      fd.append('image', file);
      return await auditSheetContext.recentAuditImageUpload(uploadId, fd);
    } catch (err) {

    }

  }

  const onAuditSubmit = async () => {
    // call audit screenshot 
    await updateCurrentAuditImage();
    // create audit sheet
    const sanitizedSheet = sanitize(sheets, providerOptions);

    // const payload = payloadreStructure(sanitizedSheet);
    const payload = prepareUpdatePayload(sanitizedSheet);

    // this api only called when submitting audit sheet responsible for changing chart status
    const response = await auditSheetContext.updateAuditSheet(
      payload,
      uploadId
    );

    if (response) {
      ioState.off();
      navigate(ROUTES.DASHBOARD_V2);
      const toastProps = {
        header: 'Success!',
        description: `${'Audit Submitted for Review.'}`,
      };
      successToast(toast, toastProps);
    }
  };

  const prepareDeltaPayload = (sheet, key) => {
    if (!sheet[key]?.length) return sheet[key];

    // 1️Sanitize
    const sanitized = sanitize(
      [{ ...sheet, data: sheet[key] }],
      providerOptions
    );
    // 2Prepare payload (same structure as sanitize)
    const payloadResult = prepareUpdatePayload(sanitized);
    // since both return array of sheets
    return payloadResult[0].data;
  };

  // Returns true only when the sheet is known to be saved. Callers that are
  // about to discard this sheet (the footer navigator) rely on that; callers
  // that only autosave ignore it.
  const updateAuditSheet = async (sheetParams, pageChange = false) => {
    if (updatingAuditSheet) {
      // Queued behind an in-flight save, so it has not landed yet.
      setOutstandingAuditSheetUpdate({sheetParams: sheetParams, pageChange: pageChange});
      return false;
    }

    // Check if this is a delta update (has deltaPayload)
    if (sheetParams?.deltaPayload) {
      try {
        uasState.on();
        const sheet = sheetParams.deltaPayload;
        const updatedDeltaPayload = {
          ...sheet,
          created: prepareDeltaPayload(sheet, 'created'),
          updated: prepareDeltaPayload(sheet, 'updated'),
        };
        const response = await auditSheetContext.createAuditSheetDelta(
          updatedDeltaPayload, 
          uploadId
        );
        // Update local sheet data with new IDs if any rows were created
        if (response?.created_ids && response.created_ids.length > 0) {
          const currentSheet = sheets[activeSheetIndex];
          const updatedData = currentSheet.data.map(row => {
            // Find rows that need ID assignment (rows with id === null)
            if (row.id === null) {
              // Match by position or other identifier
              const createdIndex = sheetParams.deltaPayload.created.findIndex(
                createdRow => createdRow === row
              );
              if (createdIndex !== -1 && response.created_ids[createdIndex]) {
                return { ...row, id: response.created_ids[createdIndex] };
              }
            }
            return row;
          });
          
          sheets[activeSheetIndex].data = updatedData;
          setSheets([...sheets]);
        }
        
        uasState.off();
        setLastSaved(new Date());
        const currentSheet = sheets[activeSheetIndex];
        const sorted = [...currentSheet.data].sort((a, b) => {
          if (a.id === null && b.id !== null) return 1;
          if (a.id !== null && b.id === null) return -1;
          if (a.id !== null && b.id !== null) return a.id - b.id; // sort by id ascending
          return 0;
        });
        sheets[activeSheetIndex].data = sorted;
        setSheets([...sheets]);
        
      } catch (err) {
        uasState.off();
        console.error('Delta update failed:', err);
        return false;
      }
      return true;
    }

    // ── Import merge: overlay only mapped fields onto existing rows ────────────
    if (sheetParams?._importMeta) {
      const { _importMeta, ...importSheet } = sheetParams;
      sheetParams = mergeImportWithExisting(
        importSheet,
        sheets[activeSheetIndex]?.data ?? [],
        _importMeta.mappedPrefixes
      );
    }

    let sanitizedSheet = [];

    if (sheetParams) {
      sheets[activeSheetIndex] = sheetParams;
      sanitizedSheet = sanitize(sheets, providerOptions);
    } else {
      sanitizedSheet = sanitize(sheets, providerOptions);
    }
    try {
      // const reStructurePayload = payloadreStructure(sanitizedSheet);
      uasState.on();
      const payload = prepareUpdatePayload(sanitizedSheet);

      await auditSheetContext.createAuditSheet(payload, uploadId);

      if (!pageChange){
        await getSheet();
      } 

      uasState.off();
      

      // saving screenshot
      await updateCurrentAuditImage();

      setLastSaved(new Date());
      return true;
    } catch (err) {
      uasState.off();
      return false;
    }
  };

  useEffect(() => {
    if (!updatingAuditSheet && outstandingAuditSheetUpdate) {
      updateAuditSheet(...Object.values(outstandingAuditSheetUpdate));
      setOutstandingAuditSheetUpdate(null);
    }
  }, [updatingAuditSheet])

  // Adding, renaming, deleting and switching sheets lived here to serve the
  // footer sheet tabs. Those are gone and nothing else reaches this -- the Excel
  // import overwrites the one sheet rather than adding another -- so every chart
  // keeps the single "Sheet1" it is created with. `sheet_name` and `index` are
  // still sent on every update, so the backend contract is untouched.

  // An autosave is running or queued behind one. The footer navigator stays
  // locked for that window so nobody leaves a sheet mid-save.
  const isSheetSaving = updatingAuditSheet || !!outstandingAuditSheetUpdate;

  /**
   * Flushes pending work before the footer navigator leaves this sheet, the same
   * way the topbar back button does, and reports whether it is safe to go.
   *
   * Anything but OK keeps the navigator where it is -- losing an edit is far
   * worse than a click that does not navigate.
   */
  const onNavigateAwayFromSheet = async () => {
    // Nothing editable on screen, so nothing to lose.
    if (view || batch || !uploadId) return NAVIGATE_AWAY.OK;
    // Saving again now would only queue behind the running save, so it could
    // not be confirmed before we leave. Ask the user to wait for it instead.
    if (isSheetSaving) return NAVIGATE_AWAY.SAVING;
    ilState.on();
    try {
      return (await updateAuditSheet()) ? NAVIGATE_AWAY.OK : NAVIGATE_AWAY.FAILED;
    } catch (err) {
      return NAVIGATE_AWAY.FAILED;
    } finally {
      ilState.off();
    }
  };

  const updateSheet = sheetData => {
    sheets[activeSheetIndex].data = sheetData;
    setSheets([...sheets]);
  };

  const removeRows = rowIndexes => {
    const sortedIndexes = [...rowIndexes].sort((a, b) => b - a); // bottom-up so indices stay correct
    const data = [...sheets[activeSheetIndex].data];
    sortedIndexes.forEach(i => data.splice(i, 1));
    sheets[activeSheetIndex].data = data;
    setSheets([...sheets]);
  };

  const onOpenNotes = obj => {
    if (obj && Object.keys(obj).length) {
      if (obj.all) {
        delete auditSheetContext.commentFilters.sheet_name;
        auditSheetContext.setCommentFilters({
          ...auditSheetContext.commentFilters,
          page: 1,
        });
      } else if (obj.current) {
        auditSheetContext.setCommentFilters({
          ...auditSheetContext.commentFilters,
          sheet_name: sheets[activeSheetIndex].sheet_name,
          page: 1,
        });
      }
    } else {
      auditSheetContext.setCommentFilters({
        ...auditSheetContext.commentFilters,
        uploadId,
        batchId,
        sheet_name: sheets[activeSheetIndex].sheet_name,
        reverse: true,
        page: 1,
      });
    }
  };

  function handleSheetClick(e) {
    setCoords({
      x:e.clientX,
      y:e.clientY
    })
  }

  function renderTopBar() {
    return (
      <Box w={'100%'} ref={topbar}>
        <Topbar
          onAuditSubmit={async () => {
            if (currentUpload?.status === 'REBUTTAL IN PROGRESS') {
              const hasOpenRebuttals = allComments.some(c => {
                if (c.parent || c.action !== 'REBUTTAL') return false;
                const replies = c.reply_comments || [];
                let status = 'Open';
                for (const r of replies) {
                  if (r.action === 'REBUTTAL-ACCEPTED') status = 'Accepted';
                  else if (r.action === 'REBUTTAL-REJECTED') status = 'Rejected';
                  else if (r.action === 'REBUTTAL-RESOLVED') status = 'Closed';
                  else if (r.action === 'RE-OPENED') status = 'Accepted';
                }
                return status === 'Accepted';
              });
              if (hasOpenRebuttals) {
                toast.showError({ description: 'Please close all accepted rebuttals before submitting.' });
                return;
              }
              const validation = await getRebuttalSubmitValidation(uploadId);
              if (!validation?.can_submit) {
                toast.showError({ description: validation?.message || 'Close all accepted rebuttals before submitting.' });
                return;
              }
            }
            ioState.on();
          }}
          batchId={batchId}
          batchName={batchName}
          updateAuditSheet={async params => {
            await updateAuditSheet(params);
          }}
          handleGoingBack={async params => {
            await updateAuditSheet(params, true);
          }}
          sheet={sheets[activeSheetIndex]}
          view={view}
          disableSubmit={isSubmitDisabled}
          lastSaved={lastSaved}
          onChange={on => {
            if (on) {
              ilState.on();
            } else {
              ilState.off();
            }
          }}
          onOpenNotes={onOpenNotes}
          notesDrawerRef={notesDrawerRef}
        />
      </Box>
    );
  }

  function renderAuditSheet() {
    return (
      <Box id="domEl" ref={domEl} onClick={handleSheetClick} >
        <AuditSheet
          height={
            window.innerHeight -
            topbar?.current?.clientHeight -
            footer?.current?.clientHeight
          }
          scale={scale}
          sheet={sheets[activeSheetIndex]}
          uploadId={uploadId}
          view={view}
          batch={batch}
          updateAuditSheet={updateAuditSheet}
          updateSheet={updateSheet}
          onCommentClick={onCommentClick}
          onSelectionEnd={onSelectionEnd}
          removeRows={removeRows}
          closeComment={() => icoState.off()}
          getSheet={getSheet}
        />
      </Box>
    );
  }

  function renderFooter() {
    return (
      <Box w={'100%'} position={'fixed'} bottom={0} ref={footer}>
        <AuditSheetFooter
          onZoom={onZoom}
          providerOptions={providerOptions}
          currentUpload={currentUpload}
          updateAuditSheet={updateAuditSheet}
          view={view}
          batchId={batchId || currentUpload?.batch_id || chartBatchId}
          currentChartId={uploadId}
          isBatchView={!!batch}
          onNavigateAway={onNavigateAwayFromSheet}
          isSaving={isSheetSaving}
        />
      </Box>
    );
  }

  function renderSubmitModal() {
    return (
      <SubmitModal
        isOpen={isOpen}
        onClose={() => ioState.off()}
        onAuditSubmit={onAuditSubmit}
      />
    );
  }

  function renderModal() {
    return (
      <Modal size={'sm'} isOpen={isLoading} isCentered bgColor={'transparent'}>
        <ModalOverlay style={{ backgroundColor: AppColors.lightSkyBlue }} />
        <ModalContent bgColor={'transparent'} shadow={'none'}>
          <ModalBody style={{ alignSelf: 'center' }}>
            <Spinner />
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  function renderCommentBox() {
    return (
      <CommentBox
        showMenu={isCommentOpen}
        metaData={metaData}
        closeComment={() => icoState.off()}
        coords={coords}
      />
    );
  }
  
  return (
    (isAuditSheetLoading ? (
      <VStack height="100vh" justify="center" align="center">
        <Spinner size="xl" />
        <Text>Loading...</Text>
      </VStack>
    ) :
    (!isAccessDenied ? (
      <Box h={'100vh'} bgColor={AppColors.bgColor}>
      {renderTopBar()}

      {renderAuditSheet()}

      {renderFooter()}

      {renderSubmitModal()}

      {renderModal()}

      {renderCommentBox()}
    </Box>

    ) : <AccessDenied
        errMsg={errMsg}/>
  )
  ));
};
export default withProvider(AuditSheetProvider, AuditSheetContainer);
